<template>
  <div class="search_box">
    <div class="search-menu-trigger" @click="openDialog">
      <Icon icon="ep:search" class="search-icon" />
      <span class="search-text">搜索</span>
    </div>
    <Dialog
      v-model="dialogVisible"
      title="菜单搜索"
      :max-height="600"
      :fullscreen="false"
      width="600px"
      style="height: 600px"
      :z-index="5000"
    >
      <p class="search-tips">点击菜单名称跳转对应页面</p>
      <div class="search-menu-content">
        <el-input
          v-model="searchKeyword"
          placeholder="请输入菜单名称"
          clearable
          size="large"
          @input="handleSearch"
          @keyup.enter="handleSearch"
          ref="searchInputRef"
        >
          <template #prefix>
            <Icon icon="ep:search" />
          </template>
        </el-input>

        <div class="search-results" v-if="searchResults.length > 0">
          <el-scrollbar max-height="400px">
            <div
              v-for="item in searchResults"
              :key="item.id || item.path"
              class="menu-item"
              @click="goToMenu(item)"
            >
              <Icon :icon="item.meta?.icon || 'ep:menu'" class="menu-icon" />
              <div class="menu-info">
                <!-- <div class="menu-title">{{ item.meta?.title }}</div> -->
                <div class="menu-title">{{ item.meta.title }}</div>
              </div>
            </div>
          </el-scrollbar>
        </div>

        <div v-else-if="searchKeyword && !searchResults.length" class="no-results">
          <Icon icon="ep:warning" class="no-results-icon" />
          <div>没有找到匹配的菜单</div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Dialog } from '@/components/Dialog'
import { Icon } from '@/components/Icon'
import { usePermissionStore } from '@/store/modules/permission'

const router = useRouter()
const permissionStore = usePermissionStore()

const dialogVisible = ref(false)
const searchKeyword = ref('')
const searchResults = ref([])
const searchInputRef = ref()

// 从用户权限获取侧边菜单数据
const allMenuList = computed(() => {
  const routers = permissionStore.getRouters
  return flattenMenu(routers)
})

// 将树形菜单扁平化
function flattenMenu(routes: any[], parentPath = ''): any[] {
  let result: any[] = []

  for (const route of routes) {
    // 构建完整路径
    const fullPath = route.path.startsWith('/') ? route.path : `${parentPath}/${route.path}`

    // 跳过按钮类型的菜单
    if (route.type === 'btn') continue

    // 将当前菜单添加到结果中
    result.push({
      ...route,
      fullPath: fullPath.replace(/\/+/g, '/') // 去除重复的斜杠
    })

    // 递归处理子菜单
    if (route.children && route.children.length > 0) {
      result = result.concat(flattenMenu(route.children, fullPath))
    }
  }

  return result
}

// 搜索菜单
const handleSearch = () => {
  if (!searchKeyword.value.trim()) {
    searchResults.value = []
    return
  }

  // 使用用户权限的侧边菜单数据进行搜索
  const keyword = searchKeyword.value.toLowerCase()

  // 过滤菜单数据，优先匹配title
  searchResults.value = allMenuList.value.filter(
    (menu) =>
      (menu.title && menu.title.toLowerCase().includes(keyword)) ||
      (menu.meta?.title && menu.meta.title.toLowerCase().includes(keyword)) ||
      (menu.path && menu.path.toLowerCase().includes(keyword))
  )
}

// 跳转到菜单
const goToMenu = (menu: any) => {
  // 使用预先构建好的fullPath
  let targetPath = menu.fullPath

  // 如果是目录类型(catalogue)，跳转到第一个子菜单
  if (menu.type === 'catalogue' || menu.type === 0) {
    if (menu.children && menu.children.length > 0) {
      const firstChild = menu.children[0]
      // 递归查找第一个子菜单的完整路径
      const findFirstLeafPath = (node: any, parentPath: string): string => {
        const currentPath = node.path.startsWith('/') ? node.path : `${parentPath}/${node.path}`
        if (node.children && node.children.length > 0) {
          return findFirstLeafPath(node.children[0], currentPath)
        }
        return currentPath.replace(/\/+/g, '/')
      }
      targetPath = findFirstLeafPath(firstChild, menu.fullPath)
    }
  }

  // 跳转到目标路径
  router.push(targetPath)

  dialogVisible.value = false
  searchKeyword.value = ''
  searchResults.value = []
}

// 打开弹窗
const openDialog = async () => {
  dialogVisible.value = true
  await nextTick()
  searchInputRef.value?.focus()
}

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false
  searchKeyword.value = ''
  searchResults.value = []
}

// 监听ESC键关闭弹窗
onMounted(() => {
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && dialogVisible.value) {
      closeDialog()
    }
  }

  document.addEventListener('keydown', handleEsc)

  // 组件销毁时移除事件监听
  onUnmounted(() => {
    document.removeEventListener('keydown', handleEsc)
  })
})
</script>

<style scoped lang="less">
.search_box {
  background: #f4f4f5;
  border-radius: 20px;
}

.search-menu-trigger {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  cursor: pointer;
  border-radius: 20px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--left-menu-bg-active-color);
  }

  .search-icon {
    margin-right: 8px;
  }

  .search-text {
    font-size: 14px;
    color: var(--el-text-color-regular);
  }
}

.search-tips {
  padding: 5px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #fff;
  background-color: var(--el-color-primary);
}

.search-menu-content {
  .search-results {
    margin-top: 20px;
  }

  .menu-item {
    display: flex;
    padding: 12px 16px;
    margin-bottom: 8px;
    cursor: pointer;
    background-color: aliceblue;
    border-radius: 4px;
    transition: background-color 0.2s;
    align-items: center;

    &:hover {
      background-color: var(--el-color-primary);

      .menu-title {
        color: #fff !important;
      }

      .menu-icon {
        color: #fff !important;
      }
    }

    .menu-icon {
      margin-right: 12px;
      font-size: 18px;
      color: var(--el-text-color-secondary);
    }

    .menu-info {
      flex: 1;

      .menu-title {
        margin-bottom: 4px;
        font-size: 14px;
        font-weight: 500;
        color: var(--el-text-color-primary);
      }

      .menu-path {
        font-size: 12px;
        color: var(--el-text-color-secondary);
      }
    }
  }

  .no-results {
    padding: 40px 0;
    color: var(--el-text-color-secondary);
    text-align: center;

    .no-results-icon {
      margin-bottom: 12px;
      font-size: 24px;
    }
  }
}
</style>
